<script lang="ts">
  import PricingModule from "./pricing_module.svelte"
  import UniversityPricingModule from "./university_pricing_module.svelte"
  import { WebsiteName } from "./../../../config"
  import { writable, type Writable } from "svelte/store"
  import { getContext } from "svelte"

  // Create a local writable store with a default value
  const audience = writable<"students" | "universities">("students")

  // Get the audience store from the layout context and subscribe to it
  const contextAudience =
    getContext<Writable<"students" | "universities">>("audience")
  if (contextAudience) {
    contextAudience.subscribe((value: "students" | "universities") => {
      audience.set(value)
    })
  }
</script>

<svelte:head>
  <title>Pricing</title>
  <meta name="description" content="Pricing - {WebsiteName}" />
</svelte:head>

<div class="min-h-[70vh] pb-8 pt-[5vh] px-4">
  <h1 class="text-3xl font-bold text-center">
    {#if $audience === "students"}
      Accelerate your interview prep
    {:else}
      Transform your university's finance program
    {/if}
  </h1>
  <h2 class="text-xl text-center text-slate-500 mt-1 pb-3">
    {#if $audience === "students"}
      Choose the plan that's right for you
    {:else}
      Tailored solutions for educational institutions
    {/if}
  </h2>

  <div class="w-full my-8">
    {#if $audience === "students"}
      <PricingModule callToAction="Start Now" highlightedPlanId="premium" />
      <p class="text-center text-sm text-gray-500 mt-4">
        Cancel anytime. No hidden fees.
      </p>
    {:else}
      <UniversityPricingModule
        callToAction="Schedule a Partnership Consultation"
      />
      <p class="text-center text-sm text-gray-500 mt-4">
        Custom plans available for institutions of all sizes.
      </p>
    {/if}

    <h1 class="text-2xl font-bold text-center mt-24">Pricing FAQ</h1>
    <div class="flex place-content-center">
      <div class="join join-vertical max-w-xl py-6 mx-auto">
        {#if $audience === "students"}
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" checked />
            <div class="collapse-title text-lg font-medium">
              What features are included?
            </div>
            <div class="collapse-content">
              <p>
                Our plans include access to AI-powered mock interviews,
                personalized feedback, and our adaptive learning system. Choose
                the plan that best fits your needs to start accelerating your
                interview preparation today.
              </p>
            </div>
          </div>
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" />
            <div class="collapse-title text-lg font-medium">
              Can I switch between plans?
            </div>
            <div class="collapse-content">
              <p>
                Yes! You can upgrade or downgrade your plan at any time. When
                upgrading, you'll get immediate access to additional features.
                If you downgrade, the changes will take effect at the start of
                your next billing cycle.
              </p>
            </div>
          </div>
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" />
            <div class="collapse-title text-lg font-medium">
              How do the coaching calls work?
            </div>
            <div class="collapse-content">
              <p>
                Basic plan members get access to one monthly group coaching call
                with our interview experts. Premium members receive weekly
                coaching calls, providing more frequent opportunities for
                personalized guidance and Q&A sessions.
              </p>
            </div>
          </div>
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" />
            <div class="collapse-title text-lg font-medium">
              Can I cancel my subscription anytime?
            </div>
            <div class="collapse-content">
              <p>
                Absolutely! You can cancel your subscription at any time from
                your account settings. After cancellation, you'll continue to
                have access until the end of your current billing period.
              </p>
              <p class="mt-4">
                There are no cancellation fees or hidden charges. We believe in
                making it easy to manage your subscription on your terms.
              </p>
            </div>
          </div>
        {:else}
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" checked />
            <div class="collapse-title text-lg font-medium">
              What is the typical implementation timeline?
            </div>
            <div class="collapse-content">
              <p>
                Our streamlined process typically takes 2-4 weeks, depending on
                your institution's specific needs and integration requirements.
                Our dedicated support team ensures a smooth onboarding process,
                minimizing disruption to your existing programs.
              </p>
            </div>
          </div>
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" />
            <div class="collapse-title text-lg font-medium">
              How does the integration process work?
            </div>
            <div class="collapse-content">
              <p>
                We offer seamless integration with popular Learning Management
                Systems like Canvas, Blackboard, and Moodle. Our team will work
                closely with your IT department to ensure Ace The Round fits
                perfectly into your existing digital ecosystem, including single
                sign-on capabilities if required.
              </p>
            </div>
          </div>
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" />
            <div class="collapse-title text-lg font-medium">
              What ROI and success metrics can we expect?
            </div>
            <div class="collapse-content">
              <p>
                Universities partnering with Ace The Round typically see a
                15-30% increase in finance job placements within the first year.
                Other key metrics include improved student satisfaction scores,
                higher average starting salaries, and strengthened relationships
                with top employers in the finance sector.
              </p>
            </div>
          </div>
          <div class="collapse collapse-arrow join-item border border-primary">
            <input type="radio" name="faq-accordion" />
            <div class="collapse-title text-lg font-medium">
              How do you ensure student data privacy and security?
            </div>
            <div class="collapse-content">
              <p>
                We take data privacy very seriously. Ace The Round is fully
                FERPA compliant and adheres to the highest standards of data
                protection. All student data is encrypted both in transit and at
                rest, and we provide detailed data handling agreements as part
                of our partnership contracts.
              </p>
            </div>
          </div>
        {/if}
      </div>
    </div>

    <svg style="display:none" version="2.0">
      <defs>
        <symbol
          id="checkcircle"
          viewBox="0 0 24 24"
          stroke-width="2"
          fill="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M16.417 10.283A7.917 7.917 0 1 1 8.5 2.366a7.916 7.916 0 0 1 7.917 7.917zm-4.105-4.498a.791.791 0 0 0-1.082.29l-3.828 6.63-1.733-2.08a.791.791 0 1 0-1.216 1.014l2.459 2.952a.792.792 0 0 0 .608.285.83.83 0 0 0 .068-.003.791.791 0 0 0 .618-.393L12.6 6.866a.791.791 0 0 0-.29-1.081z"
          />
        </symbol>
      </defs>
    </svg>

    <svg style="display:none" version="2.0">
      <defs>
        <symbol id="nocircle" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm4,11H8a1,1,0,0,1,0-2h8a1,1,0,0,1,0,2Z"
          />
        </symbol>
      </defs>
    </svg>
  </div>
</div>
